import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { adminApi } from '../../api/adminApi';

interface NewsItem {
  id: number;
  title: string;
  content: string;
}

export default function NewsAdmin() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [news, setNews] = useState<NewsItem[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const quillRef = useRef<ReactQuill | null>(null);

  // --- Стан для кастомного модального вікна ---
  const [modal, setModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm?: () => void;
    isConfirm?: boolean;
  }>({ show: false, title: '', message: '' });

  const showAlert = (title: string, message: string) => {
    setModal({ show: true, title, message, isConfirm: false });
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setModal({ show: true, title, message, onConfirm, isConfirm: true });
  };

  const closeModal = () => setModal(prev => ({ ...prev, show: false }));
  // -------------------------------------------

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const loadNews = async () => {
    try {
      const data = await adminApi.getNews();
      setNews(data);
    } catch (err) {
      console.error('Помилка завантаження новин:', err);
    }
  };

  useEffect(() => {
    loadNews();
  }, []);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNews = news.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(news.length / itemsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    document.getElementById('news-list-title')?.scrollIntoView({ behavior: 'smooth' });
  };

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('image', file);

      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/upload`, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });
        const data = await res.json();
        const editor = quillRef.current?.getEditor();
        if (editor && data.url) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range?.index || 0, 'image', data.url);
          setContent(editor.root.innerHTML);
        }
      } catch (err) {
        showAlert('Помилка', 'Не вдалося завантажити фото');
      }
    };
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: { image: imageHandler }
    }
  }), [imageHandler]);

  const submit = async () => {
    if (!title || !content || content === '<p><br></p>') {
      return showAlert('Увага', 'Заповніть заголовок та зміст новини!');
    }
    
    try {
      if (editingId) {
        await adminApi.updateNews(editingId, title, content);
        setNews(prev => prev.map(n => n.id === editingId ? { ...n, title, content } : n));
        showAlert('Успіх', 'Новину оновлено!');
      } else {
        const newNews = await adminApi.createNews(title, content);
        setNews(prev => [newNews, ...prev]);
        showAlert('Успіх', 'Новину опубліковано!');
        setCurrentPage(1);
      }
      resetForm();
    } catch (err) {
      showAlert('Помилка', 'Не вдалося зберегти новину');
    }
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setEditingId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startEdit = (item: NewsItem) => {
    setEditingId(item.id);
    setTitle(item.title);
    setContent(item.content);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (id: number) => {
    showConfirm('Видалення', 'Ви впевнені, що хочете видалити цю новину?', async () => {
      try {
        await adminApi.deleteNews(id);
        const updatedNews = news.filter(n => n.id !== id);
        setNews(updatedNews);
        if (currentNews.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } catch (err) {
        showAlert('Помилка', 'Не вдалося видалити новину');
      }
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto', fontFamily: 'Montserrat, sans-serif', color: '#2d5a50', backgroundColor: '#f0f7f4', minHeight: '100vh' }}>
      
      {/* КАСТОМНЕ МОДАЛЬНЕ ВІКНО */}
      {modal.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h4 style={{ marginTop: 0, color: '#2d5a50' }}>{modal.title}</h4>
            <p style={{ color: '#666', fontSize: '15px', marginBottom: '25px' }}>{modal.message}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              {modal.isConfirm && (
                <button onClick={closeModal} style={modalSecondaryBtnStyle}>Скасувати</button>
              )}
              <button 
                onClick={() => { modal.onConfirm?.(); closeModal(); }} 
                style={modalPrimaryBtnStyle}
              >
                ОК
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ql-container { border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; }
        .ql-toolbar { border-top-left-radius: 8px; border-top-right-radius: 8px; }
        .form-container { display: flex; flex-direction: column; width: 100%; box-sizing: border-box; }
        .ql-editor { font-family: 'Montserrat', sans-serif; font-size: 15px; }
      `}</style>

      <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#2d5a50' }}>
        {editingId ? '📝 Редагування новини' : '➕ Додати новину'}
      </h2>

      <div className="form-container" style={{ background: '#fff', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '40px' }}>
        <input
          placeholder="Заголовок новини..."
          value={title}
          onChange={e => setTitle(e.target.value)}
          style={{ 
            width: '100%', padding: '12px', marginBottom: '15px', borderRadius: '8px', 
            border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '16px', outline: 'none'
          }}
        />
        <div style={{ height: '350px', marginBottom: '50px', background: '#fff' }}>
          <ReactQuill 
            ref={quillRef} 
            theme="snow" 
            value={content} 
            onChange={setContent} 
            modules={modules} 
            style={{ height: '100%' }} 
          />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={submit} style={{ padding: '12px 25px', backgroundColor: '#1abc9c', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
            {editingId ? 'Зберегти зміни' : 'Опублікувати'}
          </button>
          {editingId && (
            <button onClick={resetForm} style={{ padding: '12px 25px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Скасувати
            </button>
          )}
        </div>
      </div>

      <h3 id="news-list-title" style={{ marginBottom: '20px' }}>📋 Список новин ({news.length})</h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {currentNews.map(n => (
          <div key={n.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '15px', background: '#fff', border: '1px solid #eee',
            borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <h4 style={{ margin: '0 0 5px 0', fontSize: '16px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {n.title}
              </h4>
              <span style={{ fontSize: '12px', color: '#888' }}>ID: {n.id}</span>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginLeft: '20px' }}>
              <button onClick={() => startEdit(n)} style={{ padding: '6px 12px', backgroundColor: '#e6fffa', color: '#2c7a7b', border: '1px solid #81e6d9', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                Редагувати
              </button>
              <button onClick={() => remove(n.id)} style={{ padding: '6px 12px', backgroundColor: '#fff5f5', color: '#e53e3e', border: '1px solid #feb2b2', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                Видалити
              </button>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '30px', flexWrap: 'wrap' }}>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => paginate(i + 1)}
              style={{
                width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #ddd',
                backgroundColor: currentPage === i + 1 ? '#1abc9c' : '#fff',
                color: currentPage === i + 1 ? '#fff' : '#333',
                cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.2s'
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Стилі Модального Вікна (ті самі для уніфікації) ---
const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
  justifyContent: 'center', alignItems: 'center', zIndex: 10000,
  backdropFilter: 'blur(2px)'
};

const modalContentStyle: React.CSSProperties = {
  background: '#fff', padding: '30px', borderRadius: '15px',
  maxWidth: '400px', width: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
  textAlign: 'center'
};

const modalPrimaryBtnStyle = {
  padding: '10px 25px', background: '#1abc9c', color: '#fff',
  border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' as const
};

const modalSecondaryBtnStyle = {
  padding: '10px 25px', background: '#eee', color: '#333',
  border: 'none', borderRadius: '8px', cursor: 'pointer'
};